<template>
  <MDBModal v-model="visible" tabindex="-1" centered size="xl" fullscreen="md" class="bg-modal required-consent-modal"
    staticBackdrop :keyboard="false">
    <div class="required-consent-layout bg-gray">
      <div class="required-consent-head px-3 py-2 border-bottom bg-white flex-shrink-0">
        <h5 class="modal-title mb-0 fs-6">{{ $t('consents.requiredConsentTitle') || 'Required consent' }}</h5>
      </div>

      <div class="required-consent-body">
        <div class="required-consent-top px-3 px-md-4 pt-3 pb-2">
          <Loader :show="loading" />

          <h5 v-if="!loading" class="mb-3 mb-md-4 fs-5">{{ $t('consents.title') }}</h5>

          <p v-if="error" class="text-danger fw-bold mb-0">{{ error }}</p>
        </div>

        <div v-if="!loading && !error && consents.length > 0" class="required-consent-scroll px-2 px-md-3">
          <MDBRow class="required-consent-row g-3">
            <MDBCol col="12" md="4" class="mb-3 mb-md-0">
              <ConsentList :consents="consents" :selected-consent-id="selectedConsentId"
                :read-consent-ids="readConsentIds" @select-consent="handleSelectConsent" />

              <div class="bg-white p-3 mt-2">
                <small class="text-gray">
                  {{ $t('consents.requiredConsentsCompleted', { completed: completedCount, total: totalRequiredCount })
                  }}
                </small>
                <div class="progress mt-2">
                  <div class="progress-bar bg-danger" :style="{ width: progressPercentage + '%' }"></div>
                </div>
              </div>
            </MDBCol>

            <MDBCol col="12" md="8">
              <ConsentDetails v-if="selectedConsent" :consent="selectedConsent"
                :is-read="isConsentRead(selectedConsent.id)" :question-answers="getQuestionAnswers(selectedConsent.id)"
                :constrain-for-modal="true" @read="handleConsentRead" @question-answer="handleQuestionAnswer" />
              <div v-else class="bg-white p-5 text-center">
                <p class="text-gray">{{ $t('consents.selectConsentToView') }}</p>
              </div>
            </MDBCol>
          </MDBRow>
        </div>

        <footer v-if="!loading && !error && consents.length > 0" class="required-consent-footer">
          <SaveButton block :disabled="!canSave" :loading="submitting" :error="saveError" :hint="saveBlockedHint"
            @save="handleSave" />
        </footer>
      </div>
    </div>
  </MDBModal>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from "vue";
import { useI18n } from "vue-i18n";
import { MDBModal, MDBRow, MDBCol } from "mdb-vue-ui-kit";
import { MarketingConsentService } from "@/services/marketing-consent.service";
import { CONSENT_TYPE } from "@/configs/constants";
import ConsentList from "./ConsentList.vue";
import ConsentDetails from "./ConsentDetails.vue";
import SaveButton from "./SaveButton.vue";
import Loader from "@/components/General/Loader/index.vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  consents: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  submitType: {
    type: String,
    default: CONSENT_TYPE.ONBOARDING,
  },
});

const emit = defineEmits(["update:modelValue", "accepted"]);

const { t } = useI18n();

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

const selectedConsentId = ref(null);
const readConsentIds = ref(new Set());
const questionAnswers = ref({});
const submitting = ref(false);
const saveError = ref(null);

const selectedConsent = computed(() => {
  return props.consents.find((c) => c.id === selectedConsentId.value);
});

const requiredConsents = computed(() => props.consents.filter((c) => c.required));

const totalRequiredCount = computed(() => requiredConsents.value.length);

const completedCount = computed(() => {
  return requiredConsents.value.filter((consent) => {
    const requiredQuestions = (consent.questions || []).filter((q) => q.required);
    if (requiredQuestions.length === 0) return false;

    const consentAnswers = questionAnswers.value[consent.id] || {};

    return requiredQuestions.every((q) => {
      const local = consentAnswers[q.id];
      const isQServerAnswered = q.answer !== null && q.answer !== undefined;
      if (isQServerAnswered) {
        return local !== false;
      } else {
        return local === true;
      }
    });
  }).length;
});

const progressPercentage = computed(() => {
  if (totalRequiredCount.value === 0) return 100;
  return (completedCount.value / totalRequiredCount.value) * 100;
});

const unreadConsentNames = computed(() => {
  return props.consents
    .filter((c) => {
      if (c.answered) return false;
      return !readConsentIds.value.has(c.id);
    })
    .map((c) => c.name || `Consent #${c.id}`);
});

const pendingConsentNames = computed(() => {
  return props.consents
    .filter((consent) => {
      const requiredQuestions = (consent.questions || []).filter((q) => q.required);
      if (requiredQuestions.length === 0) return false;

      const consentAnswers = questionAnswers.value[consent.id] || {};

      return requiredQuestions.some((q) => {
        const local = consentAnswers[q.id];
        const isQServerAnswered = q.answer !== null && q.answer !== undefined;

        if (isQServerAnswered) {
          return local === false;
        } else {
          return local !== true;
        }
      });
    })
    .map((c) => c.name || `Consent #${c.id}`);
});

const canSave = computed(
  () => unreadConsentNames.value.length === 0 && pendingConsentNames.value.length === 0
);

const saveBlockedHint = computed(() => {
  if (canSave.value) return null;
  if (unreadConsentNames.value.length > 0) {
    const names = unreadConsentNames.value.map((n) => `"${n}"`).join(", ");
    return t("consents.readAllContentFirst", { names });
  }
  const names = pendingConsentNames.value.map((n) => `"${n}"`).join(", ");
  return t("consents.acceptAllRequiredIn", { names });
});

const isConsentRead = (consentId) => readConsentIds.value.has(consentId);

const handleSelectConsent = (consentId) => {
  selectedConsentId.value = consentId;
  if (!selectedConsentId.value && props.consents.length > 0) {
    selectedConsentId.value = props.consents[0].id;
  }
};

const handleConsentRead = (consentId) => {
  readConsentIds.value.add(consentId);
};

const handleQuestionAnswer = ({ consentId, questionId, answered }) => {
  if (!questionAnswers.value[consentId]) {
    questionAnswers.value[consentId] = {};
  }
  questionAnswers.value[consentId][questionId] = answered;
};

const getQuestionAnswers = (consentId) => {
  return questionAnswers.value[consentId] || {};
};

const handleSave = async () => {
  if (!canSave.value) return;

  submitting.value = true;
  saveError.value = null;

  try {
    const answers = [];

    props.consents.forEach((consent) => {
      if (!consent.questions || consent.questions.length === 0) return;

      const consentAnswers = questionAnswers.value[consent.id] || {};

      consent.questions.forEach((question) => {
        const local = consentAnswers[question.id];
        const isQServerAnswered = question.answer !== null && question.answer !== undefined;

        let accepted;
        if (isQServerAnswered) {
          accepted = local !== undefined ? local === true : true;
        } else {
          accepted = local === true;
        }

        if (question.required && !accepted) return;

        answers.push({
          question_id: question.id,
          accepted,
        });
      });
    });

    await MarketingConsentService.submitConsents({
      type: props.submitType,
      answers,
    });

    emit("accepted");
  } catch (err) {
    console.error("Error saving consents:", err);
    saveError.value = err.response?.data?.message || t("consents.errorSaving");
  } finally {
    submitting.value = false;
  }
};

watch(
  () => props.consents,
  (newConsents) => {
    readConsentIds.value = new Set();
    questionAnswers.value = {};
    if (newConsents?.length) {
      newConsents.forEach((consent) => {
        questionAnswers.value[consent.id] = {};
        consent.questions?.forEach((q) => {
          if (q.accepted !== undefined) {
            questionAnswers.value[consent.id][q.id] = q.accepted;
          }
        });
      });
      if (!selectedConsentId.value) {
        selectedConsentId.value = newConsents[0].id;
      }
    } else {
      selectedConsentId.value = null;
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.required-consent-modal {
  z-index: 2000;

  :deep(.modal-dialog) {
    max-width: 90%;
    width: 90%;
    margin: 1rem auto;
  }

  :deep(.modal-content) {
    max-height: 96vh;
    min-height: auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.required-consent-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  max-height: inherit;
  overflow: hidden;
}

.required-consent-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.required-consent-top {
  flex-shrink: 0;
}

.required-consent-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.5rem;
}

.required-consent-footer {
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid #d9d9d9;
  padding: 0.75rem 1rem;
  padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.07);
}

.required-consent-row {
  align-items: stretch;

  :deep(.col-md-4),
  :deep(.col-md-8) {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  :deep(.col-md-8) {
    flex: 1;
  }
}

@media screen and (max-width: $mdBreakpoint) {
  .required-consent-modal {

    /* Nadpisz globalne 90% — pełna szerokość/wysokość (współgra z fullscreen="md"). */
    :deep(.modal-dialog) {
      margin: 0 !important;
      max-width: 100% !important;
      width: 100% !important;
      height: 100%;
      max-height: 100%;
    }

    :deep(.modal-content) {
      width: 100%;
      max-width: 100%;
      height: 100%;
      max-height: 100dvh;
      min-height: 0;
      border-radius: 0;
    }

    .required-consent-layout {
      height: 100%;
    }
  }

  .required-consent-scroll {
    padding-bottom: 0.25rem;
  }
}
</style>
